// neurotrader-admin/src/pages/AdminDashboardPage.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import YearlyEvolutionChart from '../components/YearlyEvolutionChart';

import StatCard from '../components/StatCard';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiDollarSign, FiCalendar, FiTrendingUp, FiGlobe, FiUsers, FiRepeat } from 'react-icons/fi';
import './AdminDashboardPage.css';

const AdminDashboardPage = () => {
    // Estados para os cards de totalizadores do topo
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    //Estados para grafico
    const [yearlyData, setYearlyData] = useState([]);
    const [loadingYearly, setLoadingYearly] = useState(true);

    // Estados para a seção de período personalizado
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());
    const [detailedData, setDetailedData] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const {user} = useAuth();
    console.log('[AdminDashboardPage] Renderizando. Estado inicial:', {
        user,
        stats,
        detailedData,
        loadingStats,
        loadingDetails
    });

    // Efeito para buscar os totalizadores dos cards do topo
    useEffect(() => {
        if (!user?.token) {
            console.log('[TotalStats Effect] Parou: Usuário ou token ausente.');
            return;
        }

        const fetchTotalStats = async (startDate, endDate) => {
            const period = {
                date_from: Math.floor(startDate.getTime() / 1000),
                date_to: Math.floor(endDate.getTime() / 1000)
            };
            console.log(`[TotalStats] Buscando para o período:`, {startDate, endDate, params: period});
            try {
                const {data} = await api.get('/api/admin/total-stats', {
                    headers: {Authorization: `Bearer ${user.token}`},
                    params: period
                });
                console.log(`[TotalStats] Resposta da API recebida:`, data);
                return {
                    commission: data.total_commission || 0,
                    transactions: data.total_transactions || 0
                };
            } catch (error) {
                console.error(`[TotalStats] ERRO na chamada da API:`, error.response || error.message);
                return { commission: 0, transactions: 0 };
            }
        };

        const loadAllStats = async () => {
            console.log('[TotalStats] Iniciando `loadAllStats`...');
            setLoadingStats(true);
            const today = new Date();
            const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));
            const startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const startOfYear = new Date(today.getFullYear(), 0, 1);
            const startOfProject = new Date('2024-01-01');

            const [day, month, year, total] = await Promise.all([
                fetchTotalStats(startOfDay, endOfDay),
                fetchTotalStats(startOfMonth, endOfDay),
                fetchTotalStats(startOfYear, endOfDay),
                fetchTotalStats(startOfProject, endOfDay)
            ]);

            const newStats = {day, month, year, total};
            console.log('[TotalStats] Todas as buscas concluídas. Novo estado de `stats` será:', newStats);
            setStats(newStats);
            setLoadingStats(false);
        };

        loadAllStats();
    }, [user]);

    // NOVO: useEffect para buscar os dados do gráfico anual
    useEffect(() => {
        if (!user?.token) return;
        const fetchYearlyData = async () => {
            setLoadingYearly(true);
            try {
                const { data } = await api.get('/api/admin/yearly-chart-data', {
                    headers: { Authorization: `Bearer ${user.token}` }
                });
                setYearlyData(data);
            } catch (error) {
                console.error("Erro ao buscar dados do gráfico anual:", error);
            } finally {
                setLoadingYearly(false);
            }
        };
        fetchYearlyData();
    }, [user]);

    // Função para buscar os dados detalhados do período personalizado
    const handleFetchDetailedStats = async () => {
        if (!user?.token) return;
        console.log('[DetailedStats] Botão "Buscar" clicado.');
        setLoadingDetails(true);
        setDetailedData(null);

        const period = {
            date_from: Math.floor(startDate.getTime() / 1000),
            date_to: Math.floor(endDate.getTime() / 1000)
        };
        console.log('[DetailedStats] Buscando para o período:', {startDate, endDate, params: period});

        try {
            const {data} = await api.get('/api/admin/detailed-stats', {
                headers: {Authorization: `Bearer ${user.token}`},
                params: period
            });
            console.log('[DetailedStats] Resposta da API recebida:', data);
            setDetailedData(data);
        } catch (error) {
            toast.error("Falha ao buscar dados detalhados.");
            console.error("[DetailedStats] ERRO na chamada da API:", error.response || error.message);
        } finally {
            console.log('[DetailedStats] Busca finalizada.');
            setLoadingDetails(false);
        }
    };

    console.log('[AdminDashboardPage] Antes do return. Verificando condições:', {
        loadingStats,
        stats,
        loadingDetails,
        detailedData
    });

    return (
        <div className="admin-page-container">
            <h1 className="admin-page-title">Markup</h1>

            {/* Seção de Totalizadores do Topo */}
            {loadingStats || !stats ? (
                <p>Carregando totalizadores...</p>
            ) : (
                <div className="stats-grid">
                    <StatCard
                        title="Hoje"
                        primaryValue={`$ ${stats.day.commission.toFixed(2)}`}
                        secondaryValue={`${stats.day.transactions} transações`}
                        icon={<FiCalendar />}
                    />
                    <StatCard
                        title="Este Mês"
                        primaryValue={`$ ${stats.month.commission.toFixed(2)}`}
                        secondaryValue={`${stats.month.transactions} transações`}
                        icon={<FiTrendingUp />}
                    />
                    <StatCard
                        title="Este Ano"
                        primaryValue={`$ ${stats.year.commission.toFixed(2)}`}
                        secondaryValue={`${stats.year.transactions} transações`}
                        icon={<FiGlobe />}
                    />
                    <StatCard
                        title="Total"
                        primaryValue={`$ ${stats.total.commission.toFixed(2)}`}
                        secondaryValue={`${stats.total.transactions} transações`}
                        icon={<FiDollarSign />}
                    />
                </div>
            )}

            <hr className="divider" />

            {/* --- SEÇÃO DO GRÁFICO ANUAL --- */}
            <div className="yearly-chart-wrapper">
                {loadingYearly ? (
                    <p>Carregando gráfico de evolução...</p>
                ) : (
                    <YearlyEvolutionChart data={yearlyData} />
                )}
            </div>
            {/* ----------------------------- */}

            <hr className="divider"/>

            {/* Seção de Período Personalizado */}
            <div className="custom-period-container">
                <h2 className="admin-section-title">Análise Detalhada por Período</h2>
                <div className="date-picker-group">
                    <DatePicker selected={startDate} onChange={(date) => setStartDate(date)}
                                className="date-picker-input"/>
                    <span>até</span>
                    <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="date-picker-input"/>
                    <button onClick={handleFetchDetailedStats} disabled={loadingDetails}
                            className="auth-button primary-button">
                        {loadingDetails ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>

                {loadingDetails && <p>Carregando dados do período...</p>}

                {/* --- BLOCO DE RENDERIZAÇÃO CONDICIONAL CORRIGIDO --- */}
                {detailedData && (
                    <>
                        <div className="detailed-stats-grid">
                            <StatCard title="Comissão no Período" primaryValue={`$ ${(detailedData.totalCommission || 0).toFixed(2)}`} icon={<FiDollarSign />} />
                            <StatCard title="Operações no Período" primaryValue={detailedData.totalOperations || 0} icon={<FiRepeat />} />
                            <StatCard title="Clientes Ativos" primaryValue={detailedData.totalClients || 0} icon={<FiUsers />} />
                        </div>

                        {Array.isArray(detailedData.clients) && detailedData.clients.length > 0 && (
                            <div className="clients-table-container">
                                <h3>Detalhes por Cliente</h3>
                                <table className="clients-table">
                                    <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>País</th>
                                        <th>Estado</th>
                                        <th>Conta (LoginID)</th>
                                        <th>Comissão ($)</th>
                                        <th>Operações</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {detailedData.clients.map(client => (
                                        <tr key={client.loginid}>
                                            <td data-label="Nome">{client.name || client.loginid}</td>
                                            <td data-label="País">{client.country || 'N/A'}</td>
                                            <td data-label="Estado">{client.state || 'N/A'}</td>
                                            <td data-label="Login ID">{client.loginid}</td>
                                            <td data-label="Comissão">{(client.commission || 0).toFixed(2)}</td>
                                            <td data-label="Operações">{client.operations || 0}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {(!detailedData.clients || detailedData.clients.length === 0) && !loadingDetails && (
                            <p>Nenhum dado encontrado para o período selecionado.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminDashboardPage;